import React, { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTheme, ThemePreference } from "../../contexts/ThemeContext";
import { useLearner } from "../../contexts/LearnerContext";
import { languages } from "../../data/languages";
import { ProfileHeader } from "../../components/profile/ProfileHeader";
import { Card } from "../../components/ui/Card";
import { Segmented } from "../../components/ui/Segmented";
import { Toggle } from "../../components/ui/Toggle";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { Modal } from "../../components/ui/Modal";
import { TextField } from "../../components/ui/TextField";
function Section({
  title,
  description,
  children,
  id
}: {
  title: string;
  description?: string;
  children: ReactNode;
  id?: string;
}) {
  return <section id={id} className="scroll-mt-24 grid gap-4 border-b-2 border-line/10 py-7 last:border-b-0 md:grid-cols-[240px_1fr] md:gap-10" data-id="element-1522" data-mp="2gikt602khney">
      <div data-id="element-1523" data-mp="4pacjdckgxzn">
        <h2 className="text-base font-bold text-ink" data-id="element-1524" data-mp="4p0v4e5l7z5k">{title}</h2>
        {description && <p className="mt-1 text-sm text-ink-2" data-id="element-1525" data-mp="3vvturdkqfq7z">{description}</p>}
      </div>
      <div className="space-y-5" data-id="element-1526" data-mp="3vxxy9uepyn7q">{children}</div>
    </section>;
}
export function Settings() {
  const {
    preference,
    setPreference
  } = useTheme();
  const {
    dailyGoal,
    setDailyGoal,
    user
  } = useLearner();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({
    speaking: true,
    sounds: true,
    autoplay: true,
    reminder: true,
    reviews: true,
    translatorUsed: true,
    weekly: false
  });
  const [uiLang, setUiLang] = useState("en");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const setPref = (key: keyof typeof prefs) => (v: boolean) => {
    setPrefs(p => ({
      ...p,
      [key]: v
    }));
    toast("Saved");
  };
  return <div className="mx-auto max-w-5xl px-4 py-6 md:px-8 md:py-10" data-id="element-1527" data-mp="2uf4c5sl7o0od">
      <ProfileHeader data-id="element-1528" data-mp="2ucrtfd7mpma9" />
      <Card className="mt-8 px-5 md:px-8" data-id="element-1529" data-mp="xehwgoxs0clu">
        <Section title="Appearance" description="Follows your device by default." data-id="element-1530" data-mp="xdglslko9s4n">
          <Segmented<ThemePreference> id="theme-pref" label="Theme" value={preference} onChange={setPreference} options={[{
          value: "light",
          label: "Light"
        }, {
          value: "dark",
          label: "Dark"
        }, {
          value: "system",
          label: "System"
        }]} data-id="element-1531" data-mp="12c9n7fmopsog" />
        </Section>

        <Section title="Learning" description="Tune how lessons feel." data-id="element-1532" data-mp="jdxd9rny8ert">
          <div data-id="element-1533" data-mp="jga3es3v1vfy">
            <p className="mb-2 text-[15px] font-semibold text-ink" data-id="element-1534" data-mp="odtitvcaoe9h">Daily goal</p>
            <div className="no-scrollbar -mx-1 overflow-x-auto px-1" data-id="element-1535" data-mp="ofwgn1jovbor">
              <Segmented id="goal" label="Daily goal" value={String(dailyGoal)} onChange={v => {
              setDailyGoal(Number(v));
              toast("Daily goal updated");
            }} options={[{
              value: "20",
              label: "Casual 20"
            }, {
              value: "50",
              label: "Steady 50"
            }, {
              value: "100",
              label: "Serious 100"
            }, {
              value: "150",
              label: "Intense 150"
            }]} data-id="element-1536" data-mp="te7r9eht5q81" />
            </div>
          </div>
          <Toggle checked={prefs.speaking} onChange={setPref("speaking")} label="Speaking exercises" description="Turn off when you can't talk out loud." data-id="element-1537" data-mp="3ak4z2iu4xucm" />
          <Toggle checked={prefs.autoplay} onChange={setPref("autoplay")} label="Auto-play audio" description="Play words as soon as an exercise opens." data-id="element-1538" data-mp="31khyu0d56ul5" />
          <Toggle checked={prefs.sounds} onChange={setPref("sounds")} label="Sound effects" data-id="element-1539" data-mp="2wlf69fecmgu2" />
        </Section>

        <Section title="Interface language" description="The language menus and buttons are shown in. Separate from your courses." data-id="element-1540" data-mp="2ro6nbcldm8rr">
          <label className="block max-w-xs" data-id="element-1541" data-mp="puyy9bfyocso">
            <span className="sr-only" data-id="element-1542" data-mp="pv8mathg49vq">Interface language</span>
            <select value={uiLang} onChange={e => {
            setUiLang(e.target.value);
            toast("Interface language saved");
          }} className="focus-ring w-full rounded-xl border-3 border-line bg-surface px-3.5 py-2.5 text-[15px] font-semibold text-ink" data-id="element-1543" data-mp="3jmfday2k7769">
              {languages.slice(0, 12).map(l => <option key={l.code} value={l.code} data-id="element-1544" data-mp="2zy6cy0qv3uvo">
                  {l.native}
                </option>)}
            </select>
          </label>
        </Section>

        <Section id="notifications" title="Notifications" description="Choose what shows up in your notification inbox and email." data-id="element-1545" data-mp="2qy02youdhd19">
          <Toggle checked={prefs.reminder} onChange={setPref("reminder")} label="Streak reminder" description="A nudge at 19:00 if you haven't practised yet." data-id="element-1546" data-mp="2vy5w69gaj7lg" />
          <Toggle checked={prefs.reviews} onChange={setPref("reviews")} label="Words due for review" description="When saved words are ready to practise again." data-id="element-1547" data-mp="2lyx4xoct872s" />
          <Toggle checked={prefs.translatorUsed} onChange={setPref("translatorUsed")} label="Translator words in lessons" description="When a word you looked up appears in a lesson." data-id="element-1548" data-mp="22dn4fkvhf66w" />
          <Toggle checked={prefs.weekly} onChange={setPref("weekly")} label="Weekly progress email" data-id="element-1549" data-mp="2cc4210oxej1x" />
        </Section>

        <Section title="Account" description={user.email} data-id="element-1550" data-mp="27awu91ms9jv9">
          <div className="flex flex-wrap gap-3" data-id="element-1551" data-mp="2lb7jk08g2qeb">
            <BrutalButton variant="surface" size="sm" to="/auth/reset" data-id="element-1552" data-mp="2l9wgk2mtpmo9">
              Change password
            </BrutalButton>
            <BrutalButton variant="surface" size="sm" onClick={() => navigate("/auth/signin")} data-id="element-1553" data-mp="2v1j0cnd88u2m">
              Sign out
            </BrutalButton>
          </div>
          <button type="button" onClick={() => setDeleteOpen(true)} className="focus-ring rounded-lg text-sm font-semibold text-bad underline underline-offset-4" data-id="element-1554" data-mp="2q3hfpy05kmp1">
            Delete account
          </button>
        </Section>
      </Card>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete your account?" footer={<>
            <BrutalButton variant="surface" onClick={() => setDeleteOpen(false)} data-id="element-1556" data-mp="3h1rfsp59d2te">
              Keep account
            </BrutalButton>
            <BrutalButton variant="bad" disabled={confirmText !== "DELETE"} onClick={() => {
        setDeleteOpen(false);
        toast("Account scheduled for deletion");
        navigate("/");
      }} data-id="element-1557" data-mp="3ltriuqgmkbu7">
              Delete forever
            </BrutalButton>
          </>} data-id="element-1555" data-mp="375ogdr1sjia5">
        <p data-id="element-1558" data-mp="8mpvnncclt3o">This removes all courses, saved words and progress. It can't be undone.</p>
        <TextField className="mt-4" label='Type "DELETE" to confirm' value={confirmText} onValueChange={setConfirmText} data-id="element-1559" data-mp="25f9is7rzxntr" />
      </Modal>
    </div>;
}